import { useWorkspace } from '~/contexts';
import { useFeatures } from '~/hooks';
import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { Icon, Tooltip } from '~/components';
import { colors } from '~/styles';
import { useAssistant } from '../ai/assistant/AssistantContext';
import { TimesheetProvider } from '../time/timesheets/TimesheetContext';
import EnableAssistantModal from './EnableAssistantModal';
import { HelpMenu, WorkspaceMenu, WorkspaceSearch, WorkspaceTimer } from './header';

const Header = styled.header`
  display: flex;
  align-items: center;
  position: fixed;
  top: ${({ hasMessage }) => (hasMessage ? '2.5rem' : '0')};
  left: ${({ sidebarExpanded }) => (sidebarExpanded ? '10.3125rem' : '3.125rem')};
  right: 0;
  height: 3.125rem;
  border-bottom: 1px solid ${colors.grey10};
  background-color: ${colors.white};
  z-index: 60;
`;

const Spacer = styled.div`
  flex: 1;
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 0.3125rem;
`;

const ToolbarButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  padding: 0;
  border: none;
  border-radius: 0.25rem;
  background: ${colors.white};
  color: ${colors.grey25};
  font-size: 1rem;
  font-weight: normal;
  cursor: pointer;
  transition: color 150ms;

  &:hover {
    background: ${colors.white};
    color: ${colors.primary};
  }
`;

const AskRuddrButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  height: 1.875rem;
  padding: 0 0.625rem;
  margin: 0 0.75rem 0 0.625rem;
  border: none;
  border-radius: 0.3125rem;
  background: ${({ $active }) => ($active ? colors.primary : colors.primary10)};
  color: ${({ $active }) => ($active ? colors.white : colors.black)};
  font-size: 0.875rem;
  font-weight: normal;
  cursor: pointer;
  white-space: nowrap;
  transition: background 150ms;

  .icon {
    color: ${({ $active }) => ($active ? colors.white : colors.primary)};
    font-size: 0.875rem;
  }

  &:hover {
    background: ${({ $active }) => ($active ? colors.primary : colors.primary10)};
    color: ${({ $active }) => ($active ? colors.white : colors.primary)};
  }
`;

export default function WorkspaceHeader({ sidebarExpanded }) {
  const { hasMessage, workspace } = useWorkspace();
  const features = useFeatures();
  const { isOpen: assistantOpen, togglePanel } = useAssistant();
  const history = useHistory();
  const [searchOpen, setSearchOpen] = useState(false);
  const [showEnableModal, setShowEnableModal] = useState(false);
  const [openAfterEnable, setOpenAfterEnable] = useState(false);

  const openSearch = useCallback(() => setSearchOpen(true), []);
  const closeSearch = useCallback(() => setSearchOpen(false), []);

  const { openPanel } = useAssistant();

  // Open panel after feature is enabled
  useEffect(() => {
    if (openAfterEnable && features.assistant) {
      setOpenAfterEnable(false);
      openPanel();
    }
  }, [openAfterEnable, features.assistant, openPanel]);

  const handleEnableAssistant = useCallback(() => {
    setSearchOpen(false);
    setShowEnableModal(true);
  }, []);

  // Cmd/Ctrl+K shortcut
  useEffect(() => {
    function handleKeyDown(e) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen((prev) => !prev);
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <Header hasMessage={hasMessage} sidebarExpanded={sidebarExpanded}>
      <Spacer />
      <Toolbar>
        <Tooltip message="Search" placement="bottom" offsetTop={13} Container={ToolbarButton} onClick={openSearch}>
          <Icon icon="search" />
        </Tooltip>
        <HelpMenu />
        <Tooltip
          message="What's New"
          placement="bottom"
          offsetTop={13}
          Container={ToolbarButton}
          onClick={() => history.push('/whats-new')}>
          <Icon icon="lightbulb" type="far" />
        </Tooltip>
      </Toolbar>
      {features.assistant && workspace.member.assistantEnabled && (
        <AskRuddrButton data-testid="assistant-toggle" $active={assistantOpen} onClick={togglePanel}>
          <Icon icon="sparkles" />
          Ask Ruddr
        </AskRuddrButton>
      )}
      <TimesheetProvider memberId={workspace.member.id}>
        <WorkspaceTimer />
      </TimesheetProvider>
      <WorkspaceMenu />
      {searchOpen && <WorkspaceSearch onClose={closeSearch} onEnableAssistant={handleEnableAssistant} />}
      {showEnableModal && (
        <EnableAssistantModal
          onClose={() => setShowEnableModal(false)}
          onEnabled={() => {
            setShowEnableModal(false);
            setOpenAfterEnable(true);
          }}
        />
      )}
    </Header>
  );
}
